import { useState, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { sanitizeMarkdown } from '@/features/chat/shared';
import {
  askMultiFileQuestionStream,
  askSingleFileQuestionStream,
} from '@/services/multiFileAskAIService';

export interface StreamingChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export interface StreamingChatOptions {
  mode: 'single' | 'multi';
  teamId: string;
  fileId?: string; // Required for single mode
  fileIds?: string[]; // Optional for multi mode - will auto-search if not provided
  sessionId?: string | null;
  mediaAnalysisOptions?: any;
  searchLimit?: number; // Number of files to auto-select when searching (default: 5)
}

export interface UseStreamingChatParams {
  onMessageAdd?: (message: StreamingChatMessage) => void;
  onMessageUpdate?: (content: string) => void;
  onSessionUpdate?: (sessionId: string) => void;
  onFilesSelected?: (fileIds: string[]) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export const useStreamingChat = ({
  onMessageAdd,
  onMessageUpdate,
  onSessionUpdate,
  onFilesSelected,
  onError,
  onComplete,
}: UseStreamingChatParams = {}) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentCompletion, setCurrentCompletion] = useState('');

  // AbortController for canceling streaming requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Track streaming response for partial responses
  const streamingResponseRef = useRef<string>('');

  // Store callbacks in refs to keep them stable
  const onMessageAddRef = useRef(onMessageAdd);
  const onMessageUpdateRef = useRef(onMessageUpdate);
  const onSessionUpdateRef = useRef(onSessionUpdate);
  const onFilesSelectedRef = useRef(onFilesSelected);
  const onErrorRef = useRef(onError);
  const onCompleteRef = useRef(onComplete);

  // Update refs when callbacks change
  onMessageAddRef.current = onMessageAdd;
  onMessageUpdateRef.current = onMessageUpdate;
  onSessionUpdateRef.current = onSessionUpdate;
  onFilesSelectedRef.current = onFilesSelected;
  onErrorRef.current = onError;
  onCompleteRef.current = onComplete;

  const startStreaming = useCallback(
    async (question: string, options: StreamingChatOptions) => {
      try {
        const {
          mode,
          teamId,
          fileId,
          fileIds,
          sessionId,
          mediaAnalysisOptions,
          searchLimit,
        } = options;

        // Validate required parameters
        if (mode === 'single' && !fileId) {
          throw new Error('fileId is required for single mode');
        }
        // No validation needed for fileIds in multi mode - will auto-search if empty

        // Add user message to conversation
        const userMessage: StreamingChatMessage = {
          role: 'user',
          content: question,
        };
        onMessageAddRef.current?.(userMessage);

        // Set streaming state
        setIsStreaming(true);
        setCurrentCompletion('');

        // Clear previous streaming response
        streamingResponseRef.current = '';

        // Create AbortController for cancellation
        abortControllerRef.current = new AbortController();

        // Add empty assistant message that will be updated during streaming
        const assistantMessage: StreamingChatMessage = {
          role: 'assistant',
          content: '',
        };
        onMessageAddRef.current?.(assistantMessage);

        let fullResponse = '';
        let newSessionId = sessionId || '';

        // Use appropriate streaming endpoint based on mode
        if (mode === 'single') {
          const result = await askSingleFileQuestionStream(
            teamId,
            fileId!,
            question,
            sessionId,
            (accumulatedText: string) => {
              fullResponse = accumulatedText;
              streamingResponseRef.current = accumulatedText;

              // Use flushSync for immediate rendering
              flushSync(() => {
                setCurrentCompletion(accumulatedText);
                onMessageUpdateRef.current?.(accumulatedText);
              });
            },
            {
              signal: abortControllerRef.current.signal,
              mediaAnalysisOptions,
            },
          );
          newSessionId = result.sessionId;
        } else {
          const optionsObj = {
            signal: abortControllerRef.current.signal,
            onFilesReceived: (receivedFileIds: string[]) => {
              console.log(
                '[useStreamingChat] onFilesReceived callback triggered with:',
                receivedFileIds,
              );
              onFilesSelectedRef.current?.(receivedFileIds);
            },
          };
          console.log(
            '[useStreamingChat] About to call askMultiFileQuestionStream with options:',
            {
              hasSignal: !!optionsObj.signal,
              hasOnFilesReceived: !!optionsObj.onFilesReceived,
              optionsKeys: Object.keys(optionsObj),
            },
          );
          const result = await askMultiFileQuestionStream(
            teamId,
            {
              fileIds: fileIds || [],
              question,
              sessionId: sessionId || undefined,
              searchLimit,
            },
            (accumulatedText: string) => {
              fullResponse = accumulatedText;
              streamingResponseRef.current = accumulatedText;

              // Use flushSync for immediate rendering
              flushSync(() => {
                setCurrentCompletion(accumulatedText);
                onMessageUpdateRef.current?.(accumulatedText);
              });
            },
            optionsObj,
          );
          newSessionId = result.sessionId;

          console.log(
            '[useStreamingChat] Received result from askMultiFileQuestionStream:',
            result,
          );
        }

        // Clear abort controller after successful completion
        abortControllerRef.current = null;
        streamingResponseRef.current = '';

        // Sanitize the complete response
        const sanitizedCompletion = sanitizeMarkdown(fullResponse);

        // Update with final sanitized content
        flushSync(() => {
          setCurrentCompletion(sanitizedCompletion);
          onMessageUpdateRef.current?.(sanitizedCompletion);
        });

        // Update session ID
        if (newSessionId) {
          onSessionUpdateRef.current?.(newSessionId);
        }

        onCompleteRef.current?.();
      } catch (error) {
        console.error('Streaming error:', error);

        // Clear abort controller
        abortControllerRef.current = null;

        // Check if error was due to abort
        if (error instanceof Error && error.name === 'AbortError') {
          // User cancelled - save partial response if available
          if (streamingResponseRef.current) {
            const sanitizedPartial = sanitizeMarkdown(
              streamingResponseRef.current,
            );
            flushSync(() => {
              setCurrentCompletion(sanitizedPartial);
              onMessageUpdateRef.current?.(sanitizedPartial);
            });
          }
          console.log('Request was cancelled by user');
        } else {
          onErrorRef.current?.(
            error instanceof Error ? error : new Error('Unknown error'),
          );
        }

        streamingResponseRef.current = '';
      } finally {
        setIsStreaming(false);
      }
    },
    [], // Empty deps - callbacks are accessed via refs
  );

  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;

      // Save partial response if available
      const partialResponse = streamingResponseRef.current;
      if (partialResponse) {
        const sanitizedCompletion = sanitizeMarkdown(partialResponse);
        flushSync(() => {
          setCurrentCompletion(sanitizedCompletion);
          onMessageUpdateRef.current?.(sanitizedCompletion);
        });
        streamingResponseRef.current = '';
      }

      setIsStreaming(false);
    }
  }, []); // Empty deps - callbacks are accessed via refs

  return {
    isStreaming,
    currentCompletion,
    startStreaming,
    stopStreaming,
  };
};
